import React, { useCallback } from 'react';
import {
  Button,
  Input,
  Checkbox,
  ControlLink,
  Overlay,
  Text,
  PasswordInput,
  LoadingBalls
} from '@moda/om';
import { noop, useForm } from '@moda/portal-stanchions';
import { AuthAction, AuthPayloadSuccess } from '../../lib/authenticate';
import { useAuth } from '../../hooks/useAuth';
import { SocialAuthButtons } from './SocialAuthButtons';
import { AccountLocked } from './AccountLocked';

type SignInProps = {
  title?: string;
  message?: string;
  className?: string;
  showSocialAuth?: boolean;
  defaultValues?: Partial<SignInFormValues>;
  changePayment?: () => void;
  showAuthModal?: () => void;
  hideAuthModal?: () => void;
  onClickForgotPassword?: () => void;
  onAuth: (authUser?: AuthPayloadSuccess) => void;
};

export type SignInFormValues = {
  signInEmail: string;
  signInPassword: string;
  signInRemember: boolean;
};

const SIGN_IN_TITLE = 'Have an Account?';
const SIGN_IN_MESSAGE =
  'Sign in to speed up the checkout process, check your order status and save your favorite products.';
const SIGN_IN_SUBMIT = 'SIGN IN';
export const FORGOT_PASSWORD_TEXT = 'Forgot Password?';

const VALIDATIONS = {
  signInEmail: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  },
  signInPassword: {
    required: {
      message: 'Invalid password, please try again.',
      value: true
    }
  }
};

export const SignIn: React.FC<SignInProps> = ({
  onAuth,
  className,
  showSocialAuth = true,
  defaultValues,
  showAuthModal,
  hideAuthModal,
  changePayment,
  onClickForgotPassword,
  title = SIGN_IN_TITLE,
  message = SIGN_IN_MESSAGE
}) => {
  const { error: errorAuth, handleAuth, loading } = useAuth(AuthAction.SignIn);

  const onSubmit = (values: SignInFormValues) => {
    handleAuth({
      email: values.signInEmail,
      password: values.signInPassword,
      remember: values.signInRemember
    })
      .then(onAuth)
      .then(changePayment)
      .catch(noop);
  };

  const handleCancel = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();

      hideAuthModal?.();
    },
    [hideAuthModal]
  );

  const { errors: fieldErrors, handleSubmit } = useForm({ onSubmit, validations: VALIDATIONS });

  return (
    <>
      <div className={className} data-testid="signInSection">
        <form onSubmit={handleSubmit}>
          <div className="Authentication__title">{title}</div>
          <div className="Authentication__message">{message}</div>
          <div className="Authentication__error" role="alert">
            {errorAuth?.code === 'account_locked' ? <AccountLocked /> : errorAuth?.message}
          </div>

          <div className="Authentication__fields">
            <Input
              className="Authentication__input"
              defaultValue={defaultValues?.signInEmail}
              error={fieldErrors.signInEmail?.message || errorAuth != null}
              id="signInEmail"
              data-testid="signInEmail"
              placeholder="Email"
              type="email"
            />

            <PasswordInput
              className="Authentication__input"
              error={fieldErrors.signInPassword?.message || errorAuth != null}
              id="signInPassword"
              data-testid="signInPassword"
              placeholder="Password"
            />

            <div className="Authentication__bottomFields">
              <div className="Authentication__rememberMe">
                <Checkbox
                  className="Authentication__checkbox"
                  defaultChecked={defaultValues?.signInRemember ?? true}
                  id="signInRemember"
                  data-testid="signInRemember"
                  type="checkbox"
                >
                  Remember me
                </Checkbox>
              </div>

              <div className="Authentication__forgotPassword" data-testid="signInForgotPassword">
                {onClickForgotPassword ? (
                  <ControlLink onClick={onClickForgotPassword} type="button">
                    {FORGOT_PASSWORD_TEXT}
                  </ControlLink>
                ) : (
                  <ControlLink to="/forgot-password">{FORGOT_PASSWORD_TEXT}</ControlLink>
                )}
              </div>
            </div>
          </div>

          <Button
            className="Authentication__button Authentication__button--sign-in"
            type="submit"
            data-testid="signInSubmit"
          >
            {SIGN_IN_SUBMIT}
          </Button>

          <Button
            className="Authentication__button Authentication__button--cancel-sign-in"
            onClick={handleCancel}
            secondary
            type="cancel"
          >
            Cancel
          </Button>
        </form>

        {showSocialAuth && (
          <>
            <div className="Authentication__divider" />

            <SocialAuthButtons
              action={AuthAction.SignIn}
              showAmazonMessage
              showAuthModal={showAuthModal}
              hideAuthModal={hideAuthModal}
              onAuth={onAuth}
            />

            <div className="Authentication__privacy-policy">
              <ControlLink to="/privacy" rel="noopener noreferrer" target="_blank">
                Privacy Policy
              </ControlLink>
            </div>
          </>
        )}
      </div>

      <Overlay className="Authentication__overlay" show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </>
  );
};
